import {
  LockedControl,
  Row,
  RowCounter,
  RowDetails,
  RowHeader,
  RowImage,
  RowNotice,
  RowPill,
  RowSubtitle,
  RowTitle,
  RowToggle,
} from './styles';

import { Clock, Lock_01 } from '@hqo/react-components-library/dist/icons';

import { addOnMessage } from '../add-on-message';
import { addOnSubtitle } from '../add-on-subtitle';
import { selectBuildingTimeZone } from 'store/building/selectors';
import { useSelector } from 'react-redux';

import { displayUnitPriceCents } from 'store/resource/price-tiers';
import { AddOn } from 'store/resource/types';
import React, { useCallback } from 'react';
import { formatCurrency } from 'utils/formatCurrency';
import { useCurrencyType } from 'hooks/use-currency-type.hook';
import { useIntl } from 'react-intl';
import { useLocale } from 'hooks/use-locale.hook';

interface AddOnRowProps {
  addOn: AddOn;
  quantity: number;
  onIncrease: (addOn: AddOn) => void;
  onDecrease: (addOn: AddOn) => void;
  /* RES-1311. Drop the whole line. The fixed-quantity pill's action — a decrement would leave a remainder. */
  onRemove?: (addOn: AddOn) => void;
  isExpanded: boolean;
  onToggle: (addOnId: string) => void;
  /*
   * This add-on's party is not the one the tenant committed to.
   *
   * The row derives its control state from this plus `quantity` rather than a pre-computed row flag:
   * a locked party with nothing selected has no control, while one holding a selected line keeps a
   * remove-only control, so a line can never be stranded.
   *
   * Optional deliberately: `renderRow` in this component's spec passes the props above and no more.
   */
  isPartyLocked?: boolean;
  /* RES-1398. Kept past its notice window because the booking already carries it. */
  isKept?: boolean;
  /*
   * RES-1311. This product's configured range admits exactly one quantity, so the stepper is replaced
   * by an Add/Remove pill. Optional for the same reason `isPartyLocked` is: this component's spec
   * passes the props above and no more.
   */
  isFixedQuantity?: boolean;
  /*
   * RES-1311. At the product's configured ceiling. ⚠️ STYLING ONLY — `Counter` hands this to its icon
   * and leaves the click bound, so the refusal lives in the hook's `onIncrease`. Passing it here is
   * the affordance, not the guard, and neither half may depend on the other being right.
   */
  isAtMax?: boolean;
  /*
   * RES-1241. Only read for a tiered product, to price an unselected row at the smallest quantity the
   * tenant could order rather than at 0, which matches no band. Optional for the same reason the flags
   * above are: this component's spec passes the props it needs and no more.
   */
  minQuantity?: number;
  /*
   * RES-1241. The quantity-range read is in flight, so the bands this product prices by are not known
   * yet. Adding is held until it lands — see the hook, which refuses the press for real.
   */
  isAwaitingRanges?: boolean;
  /*
   * RES-1359. The booking's start, which dates the "Not available for Sep 24" line. Optional only
   * because this component's spec passes the props it needs and no more, as the flags above are.
   */
  bookingStart?: string;
}

/*
 * One add-on, as the design renders it: an expandable row carrying an inline quantity stepper.
 *
 * ⚠️ NOT the shipped in-checkout page's shape. That one opens a details modal to set quantity; the design
 * for this step puts the stepper on the row and expands a description panel in place. So the modal family
 * (`AddOnDetails`, `DetailsModalContent`, `DetailsModalFooter`) is NOT reused here — only the stepper is.
 */
export const AddOnRow = ({
  addOn,
  quantity,
  onIncrease,
  onDecrease,
  onRemove,
  isExpanded,
  onToggle,
  isPartyLocked = false,
  isKept = false,
  isFixedQuantity = false,
  isAtMax = false,
  minQuantity = 1,
  isAwaitingRanges = false,
  bookingStart,
}: AddOnRowProps): JSX.Element => {
  const intl = useIntl();
  const buildingCurrency = useCurrencyType();
  const locale = useLocale();
  /* The BUILDING's zone, so the day named here cannot disagree with the day the booking is on. */
  const timeZone = useSelector(selectBuildingTimeZone);

  /*
   * ⭐ Per-ITEM currency, not the building's. `useCurrencyType` resolves a single currency for the whole
   * building, which is right for a room but wrong for a step that can list several vendors' products.
   * The building currency is the fallback for an add-on that carries none — every Prism and Nexudus
   * add-on, and every native one until RES-1049 ships the field.
   */
  /*
   * RES-1241. Priced at the quantity being shown rather than from the scalar `price`, so a tiered row
   * re-prices as the stepper crosses a band boundary. `minQuantity` floors an unselected row.
   */
  const price = formatCurrency(
    displayUnitPriceCents(addOn, quantity, minQuantity),
    addOn.currency ?? buildingCurrency,
    locale,
    true,
    false,
  );

  const subtitle = addOnSubtitle(addOn, { intl, price, quantity, minQuantity });

  /* Hoisted rather than inlined: `lint:changed` runs with `--max-warnings=0`, and react/jsx-no-bind is on. */
  const handleToggle = useCallback(() => onToggle(addOn.id), [onToggle, addOn.id]);

  /* Nothing to reveal means nothing to toggle — the row should not announce itself as expandable. */
  const hasDetails = Boolean(addOn.description || addOn.image_url);
  /*
   * Nothing can be ADDED from a locked party — nor while the range read is in flight, which would
   * price the line from a band that has not arrived — and the control must say so rather than accept
   * the press and discard it: a `+` that looks live and does nothing is the same defect, inverted.
   *
   * Removing stays available throughout: stepping a line back down is correct under either hold.
   */
  /* RES-1359. Read off the payload, not recomputed: helix decides it and the submit path agrees. */
  const isUnavailable = Boolean(addOn.unavailable_reason);
  const notice = addOnMessage(addOn, { intl, locale, bookingStart, timeZone, isKept });

  const canAdd = !isPartyLocked && !isAwaitingRanges && !isUnavailable;
  const handleIncrease = useCallback(() => (canAdd ? onIncrease(addOn) : undefined), [canAdd, onIncrease, addOn]);
  const handleDecrease = useCallback(() => onDecrease(addOn), [onDecrease, addOn]);
  /* Falls back to the decrement only for the shipped callers that predate `onRemove`. */
  const handleRemove = useCallback(
    () => (onRemove ? onRemove(addOn) : onDecrease(addOn)),
    [onRemove, onDecrease, addOn],
  );

  return (
    <Row data-testid={`booking-add-on-${addOn.id}`}>
      <RowHeader>
        {/*
          ⚠️ `aria-describedby` sits on the BUTTON, not on the row container. An earlier revision put it
          (and an `aria-disabled`) on the row `<div>`: a bare div computes to `role="generic"`, which
          supports only global attributes, so `aria-disabled` there has no defined semantics and is
          ignored — and a description is only announced for something focusable or a widget, which an
          unnamed div is not. Neither the a11y lint rules nor axe flag it; both came back clean over it.
        */}
        <RowToggle
          type="button"
          onClick={hasDetails ? handleToggle : undefined}
          disabled={!hasDetails}
          aria-expanded={hasDetails ? isExpanded : undefined}
          aria-controls={hasDetails ? `booking-add-on-details-${addOn.id}` : undefined}
          aria-describedby={
            // eslint-disable-next-line no-nested-ternary
            isUnavailable
              ? `booking-add-on-notice-${addOn.id}`
              : isPartyLocked && quantity === 0
              ? `booking-add-on-locked-${addOn.id}`
              : undefined
          }
          data-testid={`booking-add-on-toggle-${addOn.id}`}
        >
          <span>
            <RowTitle id={`booking-add-on-title-${addOn.id}`}>{addOn.name}</RowTitle>
            {/*
              The design also shows the owning vendor's name here. ⭐ RES-1049 now sources it:
              `owning_party_name` is resolved server-side from the company uuid, because
              catalog-service exposes no company-name field of its own. It is NOT rendered here --
              RES-1159 owns the party sections and labels each one with it. This row deliberately
              stays flat, so that ticket changes how the step behaves rather than undoing this.
            */}
            {subtitle && <RowSubtitle>{subtitle}</RowSubtitle>}
          </span>
        </RowToggle>
        {/*
          Three control states, not two, so the branch nests. Kept as a ternary rather than extracted
          because each arm carries the reasoning for its own state, and hoisting them into a helper
          separates those notes from the markup they explain.
        */}
        {/* eslint-disable-next-line no-nested-ternary */}
        {isUnavailable && quantity === 0 ? null : isPartyLocked && quantity === 0 ? (
          /*
           * The control is WITHHELD, not disabled. `Counter`'s `isIncreaseDisabled` styles its icon and
           * nothing else, so a "disabled" stepper still adds. The hook guards `onIncrease` too, so neither
           * half relies on the other.
           *
           * `role="img"` with a label, not a bare icon: a bare <span> has no ARIA naming role, so an
           * `aria-label` on one is not reliably exposed. The row's `aria-describedby` points here.
           */
          <LockedControl id={`booking-add-on-locked-${addOn.id}`} data-testid={`booking-add-on-locked-${addOn.id}`}>
            <Lock_01
              size="sm"
              variant="adminGreyDark"
              role="img"
              aria-label={intl.formatMessage({ id: 'add_on_party_locked' })}
            />
          </LockedControl>
        ) : isFixedQuantity ? (
          /*
           * RES-1311. The product's range admits exactly one quantity, so there is nothing to step. The
           * label names the action rather than the state, which is what makes this correct on a locked
           * party still holding a line: there, removal is the ONLY thing the control can do, and a
           * control reading "Added" would name the one thing it cannot.
           *
           * No `aria-pressed`: this is not a toggle reporting a state, it is two different actions
           * behind one control, and the accessible name already says which.
           */
          <RowPill
            type="button"
            id={`booking-add-on-pill-${addOn.id}`}
            isSelected={quantity > 0}
            /*
             * ⛔ `handleRemove`, never `handleDecrease`. A grandfathered line can sit ABOVE what its
             * range now admits, and stepping that down by one leaves a remainder while the control
             * says it removed the item.
             */
            onClick={quantity > 0 ? handleRemove : handleIncrease}
            /* Only the ADD face is held — at quantity > 0 this control removes, which is always safe. */
            disabled={!canAdd && quantity === 0}
            /*
             * Self-reference plus the product title, so the name announces as "Remove · Coffee" rather
             * than a bare "Remove". Without it a screen-reader user moving between pills cannot tell
             * which product each one acts on — the name is in a different control with no link to this
             * one. Done by reference rather than an `aria-label` string so it needs no new translation.
             */
            aria-labelledby={`booking-add-on-pill-${addOn.id} booking-add-on-title-${addOn.id}`}
            data-testid={`booking-add-on-pill-${addOn.id}`}
          >
            {intl.formatMessage({ id: quantity > 0 ? 'add_on_remove' : 'add_on_add' })}
          </RowPill>
        ) : (
          /*
           * A locked party still holding a selected line keeps a REMOVE-only stepper, so the line can always
           * be stepped back to zero -- this step ships no reset control.
           */
          <RowCounter
            value={quantity}
            increaseHandler={handleIncrease}
            decreaseHandler={handleDecrease}
            isIncreaseDisabled={!canAdd || isAtMax}
            isDecreaseDisabled={quantity === 0}
          />
        )}
      </RowHeader>
      {/*
        Why the row cannot be ordered, or -- where the booking still meets the window -- the lead time
        it needs. A line already added keeps its control above, so a selection is never stranded.
        The icon is decorative; labelling it would announce the sentence twice.
      */}
      {notice && (
        <RowNotice id={`booking-add-on-notice-${addOn.id}`} data-testid={`booking-add-on-notice-${addOn.id}`}>
          <Clock size="sm" variant="adminGreyDark" aria-hidden="true" />
          {notice}
        </RowNotice>
      )}
      {isExpanded && hasDetails && (
        <RowDetails id={`booking-add-on-details-${addOn.id}`} data-testid={`booking-add-on-details-${addOn.id}`}>
          {addOn.image_url && <RowImage src={addOn.image_url} alt={addOn.name} />}
          {addOn.description}
        </RowDetails>
      )}
    </Row>
  );
};
